import { ErrorBoundary } from '@datadog/browser-rum-react';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Drawer } from 'components/atoms/drawer';
import { APP_CRASH_KEY } from 'constant';
import { useAgentsSidePanel } from 'contexts/providers/agents-side-panel';
import { logger } from 'services';
import { AgentContent } from './agent-content';

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    overflow: hidden;
    height: 100%;
  }
`;

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: var(--spacing-06);
  gap: var(--spacing-04);
  background: var(--white-100);
  text-align: center;
`;

const FallbackMessage = styled.p`
  font-size: 16px;
  color: var(--gray-60);
  margin: 0;
`;

interface AgentsPanelFallbackProps {
  error: Error;
  resetError: () => void;
}

export const AgentsPanelFallback = ({
  error,
  resetError,
}: AgentsPanelFallbackProps) => {
  useEffect(() => {
    logger.error(`${APP_CRASH_KEY}: AgentsSidePanel - ${error?.message}`, {
      error,
    });
  }, [error]);

  return (
    <FallbackContainer data-cy="agents-side-panel-error-fallback">
      <FallbackMessage>
        <FormattedMessage id="agents.sidePanel.errorFallback" />
      </FallbackMessage>
      <Button
        type="primary"
        onClick={resetError}
        data-cy="agents-side-panel-error-retry"
      >
        <FormattedMessage id="agents.sidePanel.errorFallback.retry" />
      </Button>
    </FallbackContainer>
  );
};

export const AgentsSidePanel = () => {
  const { isVisible, hideAgentsSidePanel, chatId } = useAgentsSidePanel();

  const onClose = useCallback(() => {
    hideAgentsSidePanel();
  }, [hideAgentsSidePanel]);

  return (
    <StyledDrawer
      open={isVisible}
      onClose={onClose}
      width={1200}
      placement="right"
      closable
      destroyOnClose={false}
    >
      {isVisible && (
        <ErrorBoundary fallback={AgentsPanelFallback}>
          <AgentContent chatId={chatId} onBack={onClose} />
        </ErrorBoundary>
      )}
    </StyledDrawer>
  );
};
